import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { useMachineMeterQuarantine, useMe, useMyPermissions, useResolveMeterQuarantine } from '../../api/queries';
import type { QuarantinePacket } from '../../api/types';
import { useAlert, useConfirm } from '../../components/dialogs/DialogProvider';
import { meterLabel, reasonExplanation, reasonLabel } from '../revenue/SuspectDayDetailDialog';
import { fmtDateTime, fmtOrEmDash } from '../../utils/format';

/**
 * Meter quarantine review — owner ruling 2026-08-21 (OP1-SITE1-MAC1 RAM-reset
 * incident). Shows the raw meter packets around the quarantine event side by
 * side: the last packet the engine ACCEPTED, the first packet AT the event
 * (what triggered the quarantine), and the latest packet — so the reviewer can
 * see exactly how the meters moved before choosing a resolution.
 *
 * Resolutions (ARMS staff + machines.admin, both enforced server-side too):
 *   • Confirm RAM reset — replays the whole raw history: EVERY reset in the
 *     unresolved window (which may span days, and may contain several resets)
 *     mints its own machine version starting at zero, and the affected gaming
 *     days are rewritten. This is the normal choice for a meter clear.
 *   • Missed wrap — the meters wrapped their odometer during an offline gap;
 *     the move is booked as a wrap (adds the wrap delta as revenue).
 *   • Machine fault — the previous readings were garbage; re-baseline at the
 *     latest reading with no revenue booked for the move.
 *
 * Meter values are raw credit COUNTS (integers), NOT cents.
 */
export function MeterQuarantineDialog({
  open, onClose, machineId, machineLabel,
}: {
  open: boolean;
  onClose: () => void;
  machineId: string;
  machineLabel: string;
}) {
  const q = useMachineMeterQuarantine(machineId, open);
  const me = useMe();
  const perms = useMyPermissions();
  const resolveM = useResolveMeterQuarantine();
  const confirm = useConfirm();
  const alert = useAlert();

  const canResolve = !!me.data?.is_arms && !!perms.data?.codes.includes('machines.admin');
  const info = q.data;

  async function onResolve(resolution: 'ram_reset' | 'missed_wrap' | 'machine_fault') {
    const messages: Record<typeof resolution, string> = {
      ram_reset: `Confirm the meters on ${machineLabel} were RAM-reset? The full raw history is replayed: every reset in the unresolved window starts a new machine version at zero and the affected days are rewritten.`,
      missed_wrap: `Treat the move on ${machineLabel} as a missed meter wrap? The wrap delta is booked as revenue.`,
      machine_fault: `Treat the previous readings on ${machineLabel} as a machine fault? Meters re-baseline at the latest reading; no revenue is booked for the move.`,
    };
    const ok = await confirm({
      title: resolution === 'ram_reset' ? 'Confirm RAM reset' : reasonLabel(resolution),
      message: messages[resolution],
      confirmLabel: resolution === 'ram_reset' ? 'Confirm RAM reset' : reasonLabel(resolution),
    });
    if (!ok) return;
    try {
      const r = await resolveM.mutateAsync({ machineId, resolution });
      await alert({
        title: 'Quarantine resolved',
        message: resolution === 'ram_reset'
          ? `Replayed ${r.snapshotsReplayed ?? 0} packets — ${r.versionsCreated ?? 0} machine version(s), ${r.daysWritten ?? 0} day row(s) written. Revenue now accumulates from the reset.`
          : `Resolved as ${reasonLabel(resolution)}.`,
        severity: 'success',
      });
      onClose();
    } catch (e) {
      await alert({
        title: 'Could not resolve',
        message: (e as { message?: string })?.message ?? 'Failed to resolve the quarantine.',
        severity: 'error',
      });
    }
  }

  const packets: { title: string; p: QuarantinePacket | null | undefined }[] = [
    { title: 'Last accepted', p: info?.lastAccepted },
    { title: 'At event', p: info?.firstAfter },
    { title: 'Latest', p: info?.latest },
  ];
  const meterKeys = info?.firstAfter?.meters
    ? Object.keys(info.firstAfter.meters)
    : info?.lastAccepted?.meters ? Object.keys(info.lastAccepted.meters) : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Meter quarantine — {machineLabel}</DialogTitle>
      <DialogContent dividers>
        {q.isLoading && (
          <Typography variant="body2" color="text.secondary">Loading quarantine detail…</Typography>
        )}
        {q.isError && (
          <Alert severity="error">
            {(q.error as { message?: string })?.message ?? 'Failed to load quarantine detail.'}
          </Alert>
        )}
        {info && !info.quarantined && (
          <Alert severity="success">This machine is not quarantined.</Alert>
        )}

        {info?.quarantined && (
          <>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="body2" component="span">
                  <strong>Meters quarantined</strong> since {fmtDateTime(info.since ?? null)} ({info.beats ?? 0} beats held):
                </Typography>
                <Chip size="small" color="error" label={info.reason ? reasonLabel(info.reason) : 'unknown'} />
              </Stack>
              {info.reason && reasonExplanation(info.reason) && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>{reasonExplanation(info.reason)}</Typography>
              )}
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Incoming readings are recorded but NOT counted into revenue until resolved.
              </Typography>
            </Alert>

            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" aria-label="quarantine meter packets" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Meter</TableCell>
                    {packets.map(({ title, p }) => (
                      <TableCell key={title} align="right">
                        {title}
                        <Typography variant="caption" color="text.secondary" display="block">
                          {p?.receivedAt ? fmtDateTime(p.receivedAt) : fmtOrEmDash(null)}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {meterKeys.map((key) => {
                    const before = info.lastAccepted?.meters[key];
                    const after = info.firstAfter?.meters[key];
                    const shrunk = before != null && after != null && after < before;
                    return (
                      <TableRow key={key} hover>
                        <TableCell>{meterLabel(key)}</TableCell>
                        {packets.map(({ title, p }) => (
                          <TableCell
                            key={title}
                            align="right"
                            sx={shrunk && title === 'At event' ? { color: 'error.main', fontWeight: 600 } : undefined}
                          >
                            {p?.meters[key] == null ? fmtOrEmDash(null) : p.meters[key].toLocaleString()}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {info?.quarantined && canResolve && (
          <>
            <Tooltip title="Replay the raw history — every reset in the window starts a new machine version at zero; affected days are rewritten.">
              <span>
                <Button color="warning" variant="contained" disabled={resolveM.isPending}
                  onClick={() => void onResolve('ram_reset')}>
                  Confirm RAM reset
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="The meters wrapped their odometer during an offline gap — book the wrap delta as revenue.">
              <span>
                <Button variant="outlined" disabled={resolveM.isPending}
                  onClick={() => void onResolve('missed_wrap')}>
                  Missed wrap
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="The previous readings were a fault — re-baseline at the latest reading, no revenue booked.">
              <span>
                <Button variant="outlined" disabled={resolveM.isPending}
                  onClick={() => void onResolve('machine_fault')}>
                  Machine fault
                </Button>
              </span>
            </Tooltip>
          </>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
